import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import SoftButton from "@/components/SoftButton";
import {
  PROTECT_CATEGORIES,
  PROTECT_CATEGORY_ORDER,
} from "@/lib/protectData";
import {
  getProtectItems,
  addProtectItem,
  deleteProtectItem,
} from "@/lib/storage";
import { PROTECT } from "@/constants/testIds";

export default function Protect() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("people");
  const [label, setLabel] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    setItems(getProtectItems());
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      map[it.category] = map[it.category] || [];
      map[it.category].push(it);
    });
    return map;
  }, [items]);

  const handleAdd = () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    const entry = addProtectItem({ category, label: trimmed });
    setItems((it) => [entry, ...it]);
    setLabel("");
    toast.success("Added to your protect list.", {
      description: "Zen Mode will gently remind you.",
    });
    // Keep focus for fast multi-add
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleDelete = (id) => {
    deleteProtectItem(id);
    setItems((it) => it.filter((i) => i.id !== id));
  };

  const applyExample = (ex) => {
    setLabel(ex);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const cat = PROTECT_CATEGORIES[category];

  return (
    <PageContainer testid={PROTECT.root}>
      <PageHeader
        title="What you're protecting"
        subtitle="Name what matters. The app remembers — for the moments you forget."
        variant="exit"
        exitTo="/home"
        exitLabel="Exit"
        testid={PROTECT.exit}
      />

      {/* Privacy strip */}
      <div
        className="rounded-2xl p-3 mb-5 flex items-center gap-2.5"
        style={{ background: "rgba(74,32,64,0.05)" }}
      >
        <Lock size={14} style={{ color: "var(--un-plum)" }} />
        <p
          className="text-[12px] leading-snug"
          style={{ color: "var(--un-text-2)" }}
        >
          Names and notes here stay <strong>on your device only</strong>. They
          quietly personalize your Zen Mode prompts.
        </p>
      </div>

      {/* Composer */}
      <div className="un-card p-4 mb-7">
        {/* Category chips */}
        <p
          className="text-[10px] uppercase tracking-[0.28em] mb-2 px-1"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          Category
        </p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {PROTECT_CATEGORY_ORDER.map((cid) => {
            const c = PROTECT_CATEGORIES[cid];
            const Icon = c.icon;
            const active = cid === category;
            return (
              <button
                key={cid}
                data-testid={`${PROTECT.categoryPrefix}${cid}`}
                onClick={() => setCategory(cid)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium un-press transition-all"
                style={{
                  background: active ? c.accent : "rgba(74,32,64,0.05)",
                  color: active ? "#FDFBF7" : "var(--un-text-2)",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                <Icon size={11} />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Input */}
        <div className="flex gap-2">
          <input
            ref={inputRef}
            data-testid={PROTECT.input}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            placeholder={cat.placeholder}
            className="flex-1 outline-none rounded-2xl p-3 text-[15px]"
            style={{
              background: "var(--un-bg-2)",
              color: "var(--un-text-1)",
              fontFamily: "DM Sans, sans-serif",
            }}
          />
          <SoftButton
            data-testid={PROTECT.add}
            variant="primary"
            size="sm"
            onClick={handleAdd}
            disabled={!label.trim()}
            iconLeft={<Plus size={14} />}
          >
            Add
          </SoftButton>
        </div>

        {/* Example chips */}
        {cat.examples?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {cat.examples.map((ex) => (
              <button
                key={ex}
                data-testid={`${PROTECT.examplePrefix}${ex.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => applyExample(ex)}
                className="text-[11px] italic px-2.5 py-1 rounded-full un-press"
                style={{
                  background: "rgba(74,32,64,0.04)",
                  color: "var(--un-text-3)",
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "13px",
                }}
              >
                {ex}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      {items.length === 0 ? (
        <EmptyProtect />
      ) : (
        <div className="flex flex-col gap-6 mb-10">
          {PROTECT_CATEGORY_ORDER.map((cid) => {
            const list = grouped[cid];
            if (!list || list.length === 0) return null;
            const c = PROTECT_CATEGORIES[cid];
            const Icon = c.icon;
            return (
              <motion.div
                key={cid}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-2 mb-2 px-1">
                  <Icon size={13} style={{ color: c.accent }} />
                  <p
                    className="text-[10px] uppercase tracking-[0.28em] font-medium"
                    style={{
                      color: c.accent,
                      fontFamily: "Manrope, sans-serif",
                    }}
                  >
                    {c.label}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <AnimatePresence>
                    {list.map((it) => (
                      <motion.div
                        key={it.id}
                        data-testid={`${PROTECT.itemPrefix}${it.id}`}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="rounded-2xl px-4 py-3 flex items-center justify-between gap-3"
                        style={{
                          background: "#FDFBF7",
                          boxShadow: "0 4px 14px rgba(74,32,64,0.05)",
                        }}
                      >
                        <span
                          className="text-[15px] font-medium"
                          style={{
                            color: "var(--un-text-1)",
                            fontFamily: "DM Sans, sans-serif",
                          }}
                        >
                          {it.label}
                        </span>
                        <button
                          data-testid={`${PROTECT.deletePrefix}${it.id}`}
                          onClick={() => handleDelete(it.id)}
                          aria-label="Remove"
                          className="w-8 h-8 rounded-full flex items-center justify-center un-press"
                          style={{
                            background: "rgba(117,55,66,0.06)",
                            color: "var(--un-burgundy)",
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <p
        className="text-center italic text-[15px] mb-10 px-6 leading-relaxed"
        style={{
          color: "var(--un-text-3)",
          fontFamily: "Cormorant Garamond, serif",
          fontSize: "17px",
        }}
      >
        Protect what matters tonight.
      </p>
    </PageContainer>
  );
}

function EmptyProtect() {
  return (
    <div
      data-testid={PROTECT.emptyState}
      className="text-center py-8 px-4"
    >
      <div
        className="mx-auto w-14 h-14 rounded-full mb-4 flex items-center justify-center"
        style={{ background: "rgba(74,32,64,0.06)" }}
      >
        <Check size={20} style={{ color: "var(--un-plum)" }} />
      </div>
      <p
        className="italic text-[17px] leading-relaxed max-w-[300px] mx-auto"
        style={{
          color: "var(--un-text-3)",
          fontFamily: "Cormorant Garamond, serif",
        }}
      >
        Name one person, one thing, one piece of your peace. Zen Mode will
        whisper their name back when you need it most.
      </p>
    </div>
  );
}
